import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export interface NavigationState {
  url: string;
  shouldRefresh: boolean;
  timestamp: number;
}

export interface NavigationTrailEntry {
  route: string;
  at: string;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationHistoryService implements OnDestroy {
  private static readonly MAX_TRAIL_ENTRIES = 10;

  private history: NavigationState[] = [];
  // Chronological trail of recent routes for chat page-context. Unlike
  // `history`, it is never pruned on revisit — it records where the user
  // actually went, capped at MAX_TRAIL_ENTRIES.
  private trail: NavigationTrailEntry[] = [];
  private isNavigatingBack = false;
  private destroy$ = new Subject<void>();

  constructor(private router: Router) {
    this.recordTrail(this.router.url);

    // Track navigation events
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      const url = (event as NavigationEnd).urlAfterRedirects ?? (event as NavigationEnd).url;
      this.recordTrail(url);
      if (!this.isNavigatingBack) {
        // Add to history if not navigating back
        this.addToHistory((event as NavigationEnd).urlAfterRedirects);
      }
      this.isNavigatingBack = false;
    });
  }

  /** Recent routes, oldest first, consecutive duplicates collapsed. */
  getRecentTrail(limit: number = NavigationHistoryService.MAX_TRAIL_ENTRIES): NavigationTrailEntry[] {
    return this.trail.slice(-limit);
  }

  private recordTrail(route: string): void {
    if (!route) {
      return;
    }
    const last = this.trail[this.trail.length - 1];
    if (last?.route === route) {
      return;
    }
    this.trail.push({ route, at: new Date().toISOString() });
    if (this.trail.length > NavigationHistoryService.MAX_TRAIL_ENTRIES) {
      this.trail.shift();
    }
  }
  
  /**
   * Add URL to navigation history
   */
  private addToHistory(url: string): void {
    // Remove any forward history if navigating to new page
    const currentIndex = this.history.findIndex(h => h.url === url);
    if (currentIndex >= 0) {
      this.history = this.history.slice(0, currentIndex + 1);
    } else {
      this.history.push({
        url,
        shouldRefresh: true,
        timestamp: Date.now()
      });
    }
  }
  
  /**
   * Mark current page as should not refresh on return
   */
  markAsNoRefresh(url?: string): void {
    const targetUrl = url || this.router.url;
    const state = this.history.find(h => h.url === targetUrl);
    if (state) {
      state.shouldRefresh = false;
    }
  }
  
  /**
   * Check if should refresh when navigating to URL
   */
  shouldRefresh(url: string): boolean {
    const state = this.history.find(h => h.url === url);
    if (!state) return true; // Default to refresh if not in history
    
    // If coming back to this URL, check if it was marked as no refresh
    return state.shouldRefresh;
  }
  
  /**
   * Navigate back without refresh
   */
  navigateBack(): void {
    this.isNavigatingBack = true;
    const currentUrl = this.router.url;
    const currentIndex = this.history.findIndex(h => h.url === currentUrl);
    
    if (currentIndex > 0) {
      const previousUrl = this.history[currentIndex - 1].url;
      this.router.navigateByUrl(previousUrl);
    } else {
      // Use browser history if our history is empty
      window.history.back();
    }
  }
  
  /**
   * Clear history
   */
  clear(): void {
    this.history = [];
  }

  /**
   * Cleanup subscriptions on service destruction
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}